import React, { useReducer } from 'react';

import i18n from '../../i18n';
import PropTypes from 'prop-types';
import { basePageWrap } from '../BasePage';
import styles from './SearchPage.module.scss';
import SearchResult from '../../components/SearchResult';
import SearchInput from '../../components/SearchInput';
import SearchPagination from '../../components/SearchPagination';
import searchToObject from '../../utils/searchToObject';
import { dataLayerPush } from '../../utils/datalayer';
import CardEventSearch from '../../components/CardEventSearch/CardEventSearch';

const reducer = (state, action) => {
    switch (action.type) {
        case 'loading':
            return {
                ...state,
                loading: action.payload,
            };
        case 'error':
            return {
                ...state,
                error: action.payload,
            };
        case 'hide_categories':
            return {
                ...state,
                hideCategories: action.payload,
            };
        case 'has_fetched':
            return {
                ...state,
                hasFetched: action.payload,
            };
        case 'set_search_result':
            return {
                ...state,
                items: action.payload.items,
                query: action.payload.query,
                pagination: action.payload.pagination,
                currentPage: action.payload.currentPage,
                totalResults: action.payload.totalResults,
                searchTerm: action.payload.searchTerm,
                categories: action.payload.categories,
                eventCategories: action.payload.eventCategories,
                hasEvents: action.payload.hasEvents,
            };
        default:
            throw new Error(`${action.type} is not defined`);
    }
};

const initialState = {
    items: [],
    pagination: {},
    query: '',
    currentPage: 1,
    hasFetched: false,
    totalResults: 0,
    searchTerm: '',
    loading: false,
    error: false,
    hideCategories: false,
    hasEvents: false,
};

const SearchContext = React.createContext(initialState);

let currentController = null;

const SearchPage = ({
    searchData = {
        endpoint: '/api/v1/general_search/',
        initialQuery: '',
    },
    lang,
    categoryRewrites,
    eventCard,
}) => {
    const [state, dispatch] = useReducer(reducer, initialState);

    const fetchSearchResults = async (params) => {
        if (!params.q) {
            return;
        }

        if (currentController) {
            currentController.abort(); // Abort the previous request
        }

        currentController = new AbortController();
        const signal = currentController.signal;

        dispatch({ type: 'has_fetched', payload: false });
        dispatch({ type: 'loading', payload: true });
        dispatch({ type: 'error', payload: false });

        const queryString = Object.keys(params)
            .map((key) => {
                if (key !== 'category') {
                    return `${encodeURIComponent(key)}=${encodeURIComponent(
                        params[key]
                    )}`;
                }
                // Handle category by repeating the parameter with the same key for each value
                return params[key]
                    .map(
                        (value) =>
                            `${encodeURIComponent(key)}=${encodeURIComponent(
                                value
                            )}`
                    )
                    .join('&');
            })
            .join('&');

        window.history.replaceState(params, 'search', '?' + queryString);

        const searchUrl = `${searchData.endpoint}?${queryString}`;
        try {
            let res = await fetch(searchUrl, { signal });

            if (!res.ok) {
                throw new Error(`Request failed with status: ${res.status}`);
            }

            res = await res.json();

            currentController = null;

            let payload = {
                searchTerm: params.q,
                query: queryString,
                items: [],
                pagination: {},
                currentPage: null,
                totalResults: 0,
                categories: [],
                eventCategories: [],
                hasEvents: false,
            };

            if (!!Object.keys(res).length) {
                payload.items = res.items;
                payload.pagination = res.pagination;
                payload.currentPage = res.pagination.current_page;
                payload.totalResults = res.total_results;
                payload.categories = res.categories;
                payload.eventCategories = res.event_categories;
                payload.hasEvents = res.has_events;
            }

            dataLayerPush({
                event: 'globalSearch',
                searchTerm: params.q,
                searchHit: payload.totalResults,
            });

            dispatch({
                type: 'set_search_result',
                payload: payload,
            });
            dispatch({ type: 'has_fetched', payload: true });
            dispatch({ type: 'loading', payload: false });
            dispatch({ type: 'hide_categories', payload: false });
        } catch (error) {
            if (error.name === 'AbortError') {
                return;
            }

            console.error(error);
            dispatch({ type: 'has_fetched', payload: true });
            dispatch({ type: 'loading', payload: false });
            dispatch({ type: 'error', payload: true });
        }
    };

    console.log(eventCard);

    return (
        <SearchContext.Provider value={{ state, dispatch }}>
            <div className={styles['SearchPage']}>
                <div className={styles['SearchPage__InputWrapper']}>
                    <div className={styles['SearchPage__Container']} id={'top'}>
                        <h1 className={styles['SearchPage__Heading']}>
                            {i18n.t('SearchPage.heading')}
                        </h1>
                        <SearchInput
                            initialQuery={searchData.initialQuery}
                            endpoint={searchData.endpoint}
                            ctx={SearchContext}
                            fetchSearchResults={fetchSearchResults}
                        />
                    </div>
                </div>
                {state.hasEvents && !state.loading && eventCard?.title && (
                    <div
                        className={
                            styles['SearchPage__CardEventSearchWrapper']
                        }>
                        <CardEventSearch
                            {...eventCard}
                        />
                    </div>
                )}
                <div className={styles['SearchPage__ResultsWrapper']}>
                    <SearchResult
                        ctx={SearchContext}
                        categoryRewrites={categoryRewrites}
                        lang={lang}
                    />
                    {state.pagination.num_pages > 1 && (
                        <SearchPagination ctx={SearchContext} />
                    )}
                </div>
            </div>
        </SearchContext.Provider>
    );
};

SearchPage.propTypes = {
    searchData: PropTypes.shape({
        endpoint: PropTypes.string,
    }),
};

export default basePageWrap(SearchPage);
